import { useEffect, useRef, useState } from "react";
import { chatWidgetConfig } from "@/lib/chat-widget-config";

// Cache the dynamic import of the headless chat core so it loads once.
let corePromise: Promise<any> | null = null;
export const loadChatCore = (url: string) =>
  (corePromise ??= import(/* @vite-ignore */ url));

export interface ChatClient {
  subscribe: (cb: (state: any) => void) => () => void;
  setPanelOpen: (open: boolean) => void;
  open: () => Promise<void>;
  close: () => void;
  reset: () => void;
  sendMessage: (msg: { text: string; attachments?: string[] }) => Promise<void>;
  setActiveChatType: (type: "liveChat" | "emailChat" | null) => void;
  submitContactForm: (payload: Record<string, any>) => Promise<void>;
  sendFeedback: (feedback: "positive" | "negative") => Promise<void>;
  restartConversation: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  uploadAttachment: (file: File) => Promise<void>;
  removeAttachment: (id: string) => void;
  setCaptchaAnchor: (el: HTMLElement | null) => void;
}

export function useChatClient() {
  const [state, setState] = useState<any>(null);
  const [client, setClient] = useState<ChatClient | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const core = await loadChatCore(chatWidgetConfig.coreUrl);
        if (!mountedRef.current) return;
        const chatClient = core.createChatClient({
          locationId: chatWidgetConfig.locationId,
          widgetId: chatWidgetConfig.chatWidgetId,
          marketplaceUrl: chatWidgetConfig.marketplaceUrl,
          source: chatWidgetConfig.source,
        }) as ChatClient;
        if (!mountedRef.current) return;
        setClient(chatClient);
        unsub = chatClient.subscribe((s: any) => setState(s));
        // Emit initial state.
        setState((s: any) => s);
      } catch (err) {
        if (mountedRef.current) {
          setState({
            error: { message: "Chat is unavailable right now." },
          });
        }
      }
    })();
    return () => {
      mountedRef.current = false;
      if (unsub) unsub();
    };
  }, []);

  return { state, client };
}
